import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import { ThemeToggle } from '@/components/ui/theme-toggle';
import { useAuthModal } from '@/hooks/useAuthModal';
import { useAuth } from '@/hooks/useAuth';
import { useNavigation } from '@/hooks/useNavigation';
import { useTrackedBills } from '@/hooks/v2';
import { useOnboarding } from '@/hooks/useOnboarding';
import { useSwipeGesture } from '@/hooks/useSwipeGesture';
import { TourLauncher } from '@/components/onboarding/TourLauncher';
import { cn } from '@/lib/utils';
import { MobileNavDrawerContent } from './MobileNavDrawerContent';

export interface MobileNavProps {
  isAuthenticated: boolean;
  isPremium: boolean;
  currentState: string;
  availableStates: Array<{
    name: string;
    code: string;
    active?: boolean;
    coming?: boolean;
  }>;
  user?: {
    name?: string;
    email?: string;
    avatar?: string;
  };
}

export const MobileNav: React.FC<MobileNavProps> = ({
  isAuthenticated,
  isPremium,
  currentState,
  availableStates: _availableStates,
  user,
}) => {
  const { showAuthModal } = useAuthModal();
  const { signOut, isAdmin } = useAuth();
  const { trackedCount } = useTrackedBills();
  const { startTour } = useOnboarding();
  const navigate = useNavigate();
  const location = useLocation();
  const { isMobileMenuOpen, openMobileMenu, closeMobileMenu } = useNavigation();

  const triggerRef = useRef<HTMLButtonElement>(null);

  // Swipe-to-close via useSwipeGesture
  const [drawerElement, setDrawerElement] = useState<HTMLElement | null>(null);
  const drawerRef = useCallback((node: HTMLDivElement | null) => setDrawerElement(node), []);
  useSwipeGesture({ onSwipeRight: closeMobileMenu }, { touchTargetElement: drawerElement });

  const handleSignOut = async () => {
    await signOut();
    closeMobileMenu();
    navigate('/');
  };

  const handleNavigation = (to: string, external = false) => {
    if (external) {
      window.open(to, '_blank', 'noopener,noreferrer');
    } else {
      navigate(to);
    }
    closeMobileMenu();
  };

  const handleAuthAction = () => {
    showAuthModal();
    closeMobileMenu();
  };

  const handleStartTour = () => {
    closeMobileMenu();
    startTour('quick-start');
  };

  // Close menu on route change
  const isFirstRender = useRef(true);
  const closeMobileMenuRef = useRef(closeMobileMenu);
  closeMobileMenuRef.current = closeMobileMenu;

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    closeMobileMenuRef.current();
  }, [location.pathname]);

  return (
    <div className="md:hidden">
      <div className="flex items-center justify-between h-16 px-4">
        {/* Logo */}
        <Link
          to="/"
          className="flex items-center"
          onClick={() => {
            if (isMobileMenuOpen) {
              closeMobileMenu();
            }
          }}
        >
          <img src="/images/logo.png" alt="BillBee Logo" className="h-8 w-8 mr-2" />
          <span className="text-xl font-bold text-foreground">BillBee.ai</span>
        </Link>

        {/* Theme Toggle, Tour Launcher, and Hamburger Menu Button */}
        <div className="flex items-center gap-1">
          <ThemeToggle />
          <TourLauncher variant="icon" />
          <Sheet
            open={isMobileMenuOpen}
            onOpenChange={(open) => {
              if (open) {
                openMobileMenu();
              } else {
                closeMobileMenu();
              }
            }}
          >
            <SheetTrigger asChild>
              <Button
                ref={triggerRef}
                variant="ghost"
                size="icon"
                className={cn(
                  'relative h-10 w-10 transition-all duration-200',
                  'hover:bg-neutral-100 focus:bg-neutral-100 dark:hover:bg-neutral-800 dark:focus:bg-neutral-800',
                  'focus:outline-none focus:ring-2 focus:ring-warning-500 focus:ring-offset-2'
                )}
                aria-label="Toggle navigation menu"
                aria-controls="mobile-navigation-menu"
              >
                <div className="relative w-6 h-6 flex flex-col items-center justify-center">
                  <span
                    className={cn(
                      'absolute h-0.5 w-6 bg-current transition-all duration-300 ease-in-out',
                      isMobileMenuOpen ? 'rotate-45 translate-y-0' : '-translate-y-2'
                    )}
                  />
                  <span
                    className={cn(
                      'absolute h-0.5 w-6 bg-current transition-all duration-300 ease-in-out',
                      isMobileMenuOpen ? 'opacity-0 translate-x-3' : 'opacity-100 translate-x-0'
                    )}
                  />
                  <span
                    className={cn(
                      'absolute h-0.5 w-6 bg-current transition-all duration-300 ease-in-out',
                      isMobileMenuOpen ? '-rotate-45 translate-y-0' : 'translate-y-2'
                    )}
                  />
                </div>
              </Button>
            </SheetTrigger>

            <SheetContent
              ref={drawerRef}
              side="right"
              className="w-[320px] sm:w-[400px] p-0 focus:outline-none"
              id="mobile-navigation-menu"
              aria-label="Mobile navigation menu"
              aria-modal="true"
              role="dialog"
            >
              <SheetHeader className="px-6 py-4 border-b border-neutral-200 dark:border-neutral-700">
                <SheetTitle className="text-left text-lg font-semibold text-foreground">
                  Navigation
                </SheetTitle>
              </SheetHeader>

              <MobileNavDrawerContent
                isAuthenticated={isAuthenticated}
                isPremium={isPremium}
                isAdmin={isAdmin}
                currentState={currentState}
                user={user}
                trackedCount={trackedCount}
                currentPath={location.pathname}
                onNavigate={handleNavigation}
                onSignOut={handleSignOut}
                onAuthAction={handleAuthAction}
                onStartTour={handleStartTour}
              />
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </div>
  );
};
